import React, { useEffect, useId, useMemo, useRef } from "react";
import { Th, HeadInner, TitleBtn, SortCaret } from "./styles";
import StringFilter from "./stringFilter";

type HeaderProps = {
  tagData: any;
  sortKey: any;
  sortAsc: any;
  onSetSortDir: (k: any, asc: boolean) => void;
  filterValue: any;
  onChangeFilter: (k: any, v: any) => void;
  isOpen: boolean;
  onToggleOpen: (k: string | null) => void;
  onRequestClose: () => void;
};

/** 간단한 인터넷(글로브) 아이콘 */
const InternetIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12 2a10 10 0 100 20 10 10 0 000-20z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M2 12h20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M12 2c2.8 2.7 4.5 6.3 4.5 10S14.8 19.3 12 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M12 2C9.2 4.7 7.5 8.3 7.5 12S9.2 19.3 12 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const Header = ({
  tagData,
  sortKey,
  sortAsc,
  onSetSortDir,
  filterValue,
  onChangeFilter,
  isOpen,
  onToggleOpen,
  onRequestClose,
}: HeaderProps) => {
  const popupId = useId();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLButtonElement | null>(null);

  const hasFilter = !!(filterValue && String(filterValue).trim());

  useEffect(() => {
    if (!isOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      const inHeader = wrapRef.current?.contains(t as Node) ?? false;
      const inPopup = !!t?.closest(`[data-filter-popup-id="${popupId}"]`);
      if (!inHeader && !inPopup) onRequestClose();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [isOpen, onRequestClose, popupId]);

  const anchorRect = useMemo(() => {
    if (!isOpen) return null;
    const el = titleRef.current;
    return el ? el.getBoundingClientRect() : null;
  }, [isOpen]);

  const setSortDir = (asc: boolean) => {
    onSetSortDir(tagData.name, asc);
  };

  const isInternet = tagData?.name === "internet";

  return (
    <Th>
      <div ref={wrapRef} style={{ position: "relative" }}>
        <HeadInner>
          <TitleBtn
            ref={titleRef}
            type="button"
            onClick={() => onToggleOpen(tagData.name)}
            data-filtered={hasFilter || undefined}
            aria-label={isInternet ? "internet" : String(tagData.name)}
          >
            {/* ✅ internet면 아이콘으로 표시 */}
            {isInternet ? (
              <>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <InternetIcon />
                </span>
                {/* 접근성/검색용(보이진 않게) */}
                <span
                  style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: "hidden",
                    clip: "rect(0,0,0,0)",
                    whiteSpace: "nowrap",
                    border: 0,
                  }}
                >
                  internet
                </span>
              </>
            ) : (
              <span className="text">{tagData.name}</span>
            )}

            {sortKey === tagData.name && (
              <SortCaret $active={sortKey === tagData.name} $asc={sortAsc} />
            )}
          </TitleBtn>
        </HeadInner>
      </div>

      {isOpen && (
        <StringFilter
          anchorRect={anchorRect}
          columnName={tagData.name}
          filter={filterValue ?? ""}
          setFilter={(v: string) => onChangeFilter(tagData.name, v)}
          sortKey={sortKey}
          sortAsc={sortAsc}
          onSortAsc={() => setSortDir(true)}
          onSortDesc={() => setSortDir(false)}
          onClose={onRequestClose}
        />
      )}
    </Th>
  );
};

export default Header;
